import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const field = "w-full rounded-none border-0 border-b-2 border-navy/15 bg-transparent px-0 py-2.5 text-base text-navy shadow-none transition-colors focus:border-gold focus:outline-none";
const label = "mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/60";

export default function Onboarding() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [industries, setIndustries] = useState([]);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [f, setF] = useState({
    business_name: "", industry: "", state: "Ohio", county: "", structure: "LLC",
    employees: 1, contractors: 0, years_in_operation: 1,
    operation_modes: [], locations: "", services_provided: "",
    handles_sensitive_info: false, licensed_employees: false, vehicles_equipment_facilities: false,
    has_insurance: false, has_licenses: false, primary_concerns: "",
  });

  useEffect(() => {
    api.get("/api/app/onboarding").then((r) => {
      setIndustries(r.data.industries);
      if (r.data.profile) setF((p) => ({ ...p, ...r.data.profile }));
    });
  }, []);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setF({ ...f, [k]: v });
  };
  const toggleMode = (m) =>
    setF({ ...f, operation_modes: f.operation_modes.includes(m) ? f.operation_modes.filter((x) => x !== m) : [...f.operation_modes, m] });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.put("/api/app/onboarding", { ...f, employees: Number(f.employees), contractors: Number(f.contractors), years_in_operation: Number(f.years_in_operation) });
      setUser((u) => ({ ...u, onboarding_completed: true, business_name: f.business_name }));
      navigate("/app");
    } catch (err) {
      setError("Saving failed — check required fields and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="onboarding-page">
      <p className="eyebrow">Business Onboarding</p>
      <h1 className="mt-3 font-serif text-4xl font-semibold text-navy">Tell us about your business</h1>
      <p className="mt-3 text-ink/65">
        Your answers customize your risk categories, suggested SOPs, and document inventory. Plain answers are fine — you can
        update everything later.
      </p>

      <div className="mt-8 flex gap-2" data-testid="onboarding-steps">
        {["The business", "Operations", "Compliance posture"].map((s, i) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full ${step > i ? "bg-gold" : "bg-navy/10"}`} />
        ))}
      </div>

      <form onSubmit={submit} className="mt-8 rounded-lg border border-navy/10 bg-white p-8" data-testid="onboarding-form">
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className={label} htmlFor="ob-name">Business name *</label>
              <input id="ob-name" required className={field} value={f.business_name} onChange={set("business_name")} data-testid="onboarding-business-name" placeholder="Your Business LLC" />
            </div>
            <div>
              <label className={label} htmlFor="ob-industry">Industry *</label>
              <select id="ob-industry" required className={field} value={f.industry} onChange={set("industry")} data-testid="onboarding-industry">
                <option value="">Select your industry…</option>
                {industries.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className={label} htmlFor="ob-structure">Business structure *</label>
                <select id="ob-structure" className={field} value={f.structure} onChange={set("structure")} data-testid="onboarding-structure">
                  {["LLC", "Corporation", "Sole proprietorship", "Partnership", "Nonprofit", "Other"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={label} htmlFor="ob-years">Years in operation</label>
                <input id="ob-years" type="number" min="0" className={field} value={f.years_in_operation} onChange={set("years_in_operation")} data-testid="onboarding-years" />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-3">
              <div>
                <label className={label} htmlFor="ob-state">State *</label>
                <input id="ob-state" required className={field} value={f.state} onChange={set("state")} data-testid="onboarding-state" />
              </div>
              <div>
                <label className={label} htmlFor="ob-county">County</label>
                <input id="ob-county" className={field} value={f.county} onChange={set("county")} data-testid="onboarding-county" placeholder="Cuyahoga" />
              </div>
              <div>
                <label className={label} htmlFor="ob-employees">Employees</label>
                <input id="ob-employees" type="number" min="0" className={field} value={f.employees} onChange={set("employees")} data-testid="onboarding-employees" />
              </div>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className={label} htmlFor="ob-contractors">Contractors</label>
                <input id="ob-contractors" type="number" min="0" className={field} value={f.contractors} onChange={set("contractors")} data-testid="onboarding-contractors" />
              </div>
              <div>
                <label className={label} htmlFor="ob-locations">Business locations</label>
                <input id="ob-locations" className={field} value={f.locations} onChange={set("locations")} data-testid="onboarding-locations" placeholder="City, shop, mobile…" />
              </div>
            </div>
            <div>
              <label className={label}>How does the business operate?</label>
              <div className="flex flex-wrap gap-2" data-testid="onboarding-modes">
                {["Physical location", "Mobile / field work", "Remote", "Hybrid"].map((m) => (
                  <button type="button" key={m} onClick={() => toggleMode(m)} data-testid={`onboarding-mode-${m.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${f.operation_modes.includes(m) ? "border-gold bg-gold/10 text-navy" : "border-navy/15 text-ink/60 hover:border-gold/50"}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={label} htmlFor="ob-services">Services provided</label>
              <textarea id="ob-services" className={`${field} min-h-20`} value={f.services_provided} onChange={set("services_provided")} data-testid="onboarding-services" placeholder="Briefly list what you sell or deliver" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            {[
              ["handles_sensitive_info", "We handle sensitive customer information", "onboarding-sensitive"],
              ["licensed_employees", "Employees require licenses or certifications", "onboarding-licensed"],
              ["vehicles_equipment_facilities", "We operate vehicles, equipment, or regulated facilities", "onboarding-vehicles"],
              ["has_insurance", "We currently carry business insurance", "onboarding-insurance"],
              ["has_licenses", "We currently hold business licenses", "onboarding-has-licenses"],
            ].map(([key, text, tid]) => (
              <label key={key} className="flex items-center gap-3 rounded-sm border border-navy/10 bg-paper p-4 text-sm text-ink/75">
                <input type="checkbox" checked={f[key]} onChange={set(key)} className="h-4 w-4 accent-[#C5A059]" data-testid={tid} />
                {text}
              </label>
            ))}
            <div>
              <label className={label} htmlFor="ob-concerns">Primary operational concerns</label>
              <textarea id="ob-concerns" className={`${field} min-h-20`} value={f.primary_concerns} onChange={set("primary_concerns")} data-testid="onboarding-concerns" placeholder="e.g. license renewals, subcontractor insurance, documentation" />
            </div>
            <p className="rounded-sm border border-gold/40 bg-gold/5 p-4 text-xs italic leading-relaxed text-navy/70">
              Your onboarding responses customize suggestions and checklists. They are not a legal determination — your business
              remains responsible for meeting applicable requirements.
            </p>
          </div>
        )}

        {error && <p className="mt-5 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="onboarding-error">{error}</p>}

        <div className="mt-8 flex justify-between">
          {step > 1 ? (
            <button type="button" onClick={() => setStep(step - 1)} className="btn-outline" data-testid="onboarding-back">Back</button>
          ) : <span />}
          {step < 3 ? (
            <button type="button" onClick={() => setStep(step + 1)} className="btn-gold" data-testid="onboarding-next">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button type="submit" disabled={busy} className="btn-gold disabled:opacity-60" data-testid="onboarding-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />}
              Finish Setup
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
